import { Head } from "@inertiajs/react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { PageHeader } from "@/components/layouts/PageHeader"
import { CapturesTable } from "@/components/captures/CapturesTable"
import type { DashboardStats } from "@/types"
import { Radio, Activity, Clock, AlertTriangle } from "lucide-react"
import { cn } from "@/lib/utils"

interface DashboardCapture {
  id: number
  title: string | null
  status: "pending" | "delivered" | "failed"
  captured_at: string
  source: { id: number; name: string; source_type: string }
}

interface DashboardProps {
  stats: DashboardStats
  recent_captures: DashboardCapture[]
}

export default function Dashboard({ stats, recent_captures }: DashboardProps) {
  // Normalize dashboard captures to the Capture shape CapturesTable expects
  const normalizedCaptures = recent_captures.map((c) => ({
    id: c.id,
    source_id: c.source.id,
    title: c.title,
    status: c.status,
    captured_at: c.captured_at,
    delivered_at: null,
    source_name: c.source.name,
    source_type: c.source.source_type,
    external_uid: null,
    content: null,
    extracted_data: {},
    delivered_payload: null,
    delivery_response: null,
    error_message: null,
    retry_count: 0,
    raw_payload: {},
  }))

  return (
    <>
      <Head title="Dashboard" />
      <div className="space-y-6">
        <PageHeader title="Dashboard" description="Overview of your feeds and captures." />

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total Sources"
            value={stats.total_sources}
            icon={Radio}
          />
          <StatCard
            title="Enabled Sources"
            value={stats.enabled_sources}
            icon={Activity}
          />
          <StatCard
            title="Recent Captures (24h)"
            value={stats.recent_captures_24h}
            icon={Clock}
          />
          <StatCard
            title="Failed Captures"
            value={stats.failed_captures}
            icon={AlertTriangle}
            destructive={stats.failed_captures > 0}
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Recent Captures</CardTitle>
          </CardHeader>
          <CardContent>
            <CapturesTable captures={normalizedCaptures} showSource />
          </CardContent>
        </Card>
      </div>
    </>
  )
}

function StatCard({
  title,
  value,
  icon: Icon,
  destructive = false,
}: {
  title: string
  value: number
  icon: React.ComponentType<{ className?: string }>
  destructive?: boolean
}) {
  return (
    <Card className={cn(destructive && "border-destructive/50")}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className={cn("size-4 text-muted-foreground", destructive && "text-destructive")} />
      </CardHeader>
      <CardContent>
        <div className={cn("text-2xl font-bold", destructive && "text-destructive")}>
          {value}
        </div>
      </CardContent>
    </Card>
  )
}
